(ns nosco.surface.components
  "Presentation-only components of the surface runtime: the classic widget
   renderers (stat/ratio/chart/text/html + the shell, icon, error
   boundary and the `Widget` dispatch) AND the surface-specific renderers
   (ideas grid, declarative table, filter pills, controls), the widget
   skeleton, the always-mounted idea modal and the global loading/refreshing
   indicator. NO engine or global-state dependencies — everything arrives as
   props; writes leave through callback props the runtime wires to the engine.
   The widget run/click mechanics live in nosco.surface.widget-run."
  (:require
   ["react" :as react]
   ["react-useportal" :default usePortal]
   [clojure.string :as str]
   [goog.object :as gobj]
   [hx.hooks :as hooks]
   [hx.react :refer [defnc] :as hx]
   [nosco.colors :as colors]
   [nosco.icons2 :as icons2]
   [nosco.lazy :refer [lazy-component]]
   [nosco.router :as router]
   [nosco.shared.form-fields :as form-fields]
   [nosco.shared.stats :as stats]
   [nosco.shared.submission-form :as submission-form]
   [nosco.surface.helpers :as helpers]
   [nosco.surface.table :as surface-table]
   [nosco.specs.report-widgets :as widgets-spec]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.html :as ui-html]
   [nosco.ui.layouts :as layouts]
   [nosco.ui.loading :as loading]
   [nosco.ui.select3 :as select3]
   [nosco.ui.skeleton :as skeleton]
   [nosco.ui.spaces-tabs :as spaces-tabs]
   [nosco.ui.table3 :as table3]
   [nosco.ui.text :as text]
   [nosco.ui.tooltips :as tooltips]
   [nosco.util :as util]
   [nosco.util.evaluations :as eval-util]
   [nosco.util.markdown :as markdown]
   [nosco.views.userlevel.idea-card :as idea-card]
   [nosco.views.userlevel.ideas-table :as ideas-table]))

(def VegaEmbed
  "Lazy handle to the vega-embed wrapper — vega (~800KB) is fetched only
   when a :chart widget first renders."
  (lazy-component nosco.surface.vega/VegaEmbed))

(defn- interpolation-map
  "Prepare a :fn result map for {key} interpolation: numbers get the default
   locale format, everything else is str'd by stats/interpolate."
  [result]
  (into {}
        (map (fn [[k v]] [k (if (number? v) (util/number-format v) v)]))
        result))

(defn- markup-interpolation-map
  "`interpolation-map` for a MARKUP sink (:html / :md), where every value is
   HTML-escaped so it can only land as text.

   Interpolation splices values — routinely USER-submitted idea fields — into
   author markup BEFORE DOMPurify runs, and the strict profile deliberately
   keeps inline `style`, `<img>` and `data-*`. Unescaped, a hostile field value
   closes the author's attribute and writes its own: a fixed-position overlay
   covering the page, an external `<img>` beaconing every viewer's IP, or a
   forged `data-toggle-set` the container's delegated click handler then acts
   on. Sanitizing after the splice can't help — by then the value IS the markup.

   The text sink (`interpolate-texts`) must NOT use this: React escapes what it
   renders, so escaping first would show a literal `&amp;`."
  [result]
  (into {}
        (map (fn [[k v]] [k (stats/escape-html v)]))
        (interpolation-map result)))

(defn- interpolate-texts
  "Resolve :title/:subtitle, then interpolate {key} placeholders from the
   result map. Conditional/computed text: a :title / :subtitle IN the result
   map overrides the static spec value (the widget's :fn can compute them),
   then still gets {key} substitution."
  [{:keys [title subtitle hide-title]} result-map]
  (let [m (interpolation-map (if (map? result-map) result-map {}))
        title (get result-map :title title)
        subtitle (get result-map :subtitle subtitle)]
    ;; :hide-title drops the title chrome while keeping :title in the spec (a
    ;; self-explanatory or header-promoted widget) — WidgetShell then omits the
    ;; whole title row.
    [(when-not hide-title (stats/interpolate title m))
     (stats/interpolate subtitle m)]))

;; =============================================================================
;; Inline legend swatches
;; =============================================================================
;; A subtitle can fold its legend into the sentence: authors write mark tokens
;; `{kind}`, `{kind:#hex}`, or `{kind:var(--name)}` that expand into tiny
;; inline SVG swatches, so the Vega spec can drop its own legend (`:legend
;; nil`). Kinds: bar / line / dash / dot. A trailing color sets the swatch;
;; absent it inherits the text color (`currentColor`). Prefer
;; `var(--nosco-category-N)` (see _surface-html.scss) over a literal hex when
;; echoing Vega's auto-assigned categorical palette — a real DOM swatch
;; resolves CSS vars fine, unlike Vega's own detached-SVG render. The color is
;; still author-maintained convention (not verified against the mark), so it
;; can drift if the mark's actual color/domain order changes. Swatches are
;; `aria-hidden`; the words remain the accessible label.

(def ^:private swatch-style
  {:display "inline-block" :vertical-align "middle" :margin "0 1px"})

(defn bar-swatch
  "Translucent filled rounded rect, echoing a bar mark."
  [color]
  [:svg {:width 14 :height 10 :aria-hidden true :style swatch-style}
   [:rect {:width 14 :height 10 :rx 2 :fill (or color "currentColor") :fill-opacity 0.35}]])

(defn line-swatch
  "Short solid horizontal stroke, echoing a line mark."
  [color]
  [:svg {:width 16 :height 8 :aria-hidden true :style swatch-style}
   [:line {:x1 0 :y1 4 :x2 16 :y2 4 :stroke (or color "currentColor") :stroke-width 2}]])

(defn dash-swatch
  "Dashed horizontal stroke (matching Vega's [6 4]), echoing a dashed line."
  [color]
  [:svg {:width 16 :height 8 :aria-hidden true :style swatch-style}
   [:line {:x1 0 :y1 4 :x2 16 :y2 4 :stroke (or color "currentColor")
           :stroke-width 2 :stroke-dasharray "6 4"}]])

(defn dot-swatch
  "Small filled circle, echoing a point mark."
  [color]
  [:svg {:width 8 :height 8 :aria-hidden true :style swatch-style}
   [:circle {:cx 4 :cy 4 :r 4 :fill (or color "currentColor")}]])

(defn- swatch
  "Dispatch a `{kind}` token (string kind + optional #hex/var(--x) color) to
   its SVG."
  [kind color]
  (case kind
    "bar" (bar-swatch color)
    "line" (line-swatch color)
    "dash" (dash-swatch color)
    "dot" (dot-swatch color)))

(def ^:private swatch-re
  #"\{(bar|line|dash|dot)(?::(#[0-9a-fA-F]{3,8}|var\(--[a-zA-Z0-9-]+\)))?\}")

(defn expand-swatches
  "Expand `{kind}` / `{kind:#hex}` mark tokens in a subtitle string into a
   vector of interleaved strings + inline SVG swatch hiccup. Returns the string
   unchanged when it carries no tokens."
  [s]
  (if (and (string? s) (re-find swatch-re s))
    (loop [rest s out []]
      (if-let [[whole kind color] (re-find swatch-re rest)]
        (let [idx (str/index-of rest whole)]
          (recur (subs rest (+ idx (count whole)))
                 (conj out (subs rest 0 idx) (swatch kind color))))
        (conj out rest)))
    s))

;; =============================================================================
;; Shell
;; =============================================================================

(defnc WidgetIcon
  "The :icon string resolved against the generated icons2 name map
   (e.g. \"ChartPie-small\"); unrecognized strings render verbatim as text
   so emoji still work."
  [{:keys [icon color]}]
  (when icon
    (if-let [svg-id (get icons2/_ (symbol icon))]
      [icons2/Icon {:icon svg-id :color (or color colors/neutral-700)}]
      [text/Text {:variant text/Body-Sm-Bold} icon])))

(defn- help-icon
  "An (i) beside a widget title whose tooltip is the author's `:help` text —
   user-facing context for what the widget shows. nil when there is no help."
  [help]
  (when help
    [tooltips/WithTooltip {:label help}
     [:span {:style {:display "inline-flex" :cursor "help" :color colors/neutral-400}}
      [icons2/Icon {:icon icons2/Info-small}]]]))

(defnc WidgetShell
  "Common widget chrome, in two variants via `:bare?`:
     - FULL (default): a bordered card — title row on top, icon beside the
       content, lighter subtitle beneath. `:on-clear` (when the widget is an
       active cross-filter) renders a ✕ next to the title.
     - BARE: no card, no icon/subtitle — just the title row above the children
       in a plain column, for widgets that draw their own layout (ideas list,
       table, filter, date-range).
   `:title` + `:hide-title` are resolved here, so 'show the title' has ONE
   definition and the title/help row lives in ONE place for every widget."
  [{:keys [icon color title subtitle help hide-title on-clear bare? children]}]
  (let [show-title? (and title (not hide-title))
        ;; the title + help row, shared by both variants. Omitted when there's
        ;; nothing to show — no visible title, no :help, no cross-filter ✕.
        title-row (when (or show-title? on-clear help)
                    [layouts/Row {:gap "8px" :class ["align-items-center" "justify-content-between"]}
                     [layouts/Row {:gap "4px" :class "align-items-center" :style {:min-width 0}}
                      (when show-title?
                        [text/Text {:variant text/Body-Sm-Bold :class "text-neutral-700"} title])
                      (help-icon help)]
                     (when on-clear
                       [buttons2/Button {:variant buttons2/NewIcon
                                         :size buttons2/SizeSmall
                                         :icon icons2/Close-small
                                         :on-click on-clear}])])]
    (if bare?
      [layouts/Column {:gap "8px"} title-row children]
      ;; height:100% + the content row growing lets cards sharing a grid row
      ;; line up to equal height (the surface stretches cells; the panel's
      ;; column ignores height so this is a no-op there)
      [layouts/Column {:gap "8px"
                       :style {:border (str "1px solid " colors/neutral-100)
                               :border-radius "8px"
                               :padding "12px"
                               :height "100%"
                               :box-sizing "border-box"}}
       title-row
       ;; align-items-stretch (not center) lets the content fill the row's height
       ;; so a chart inherits a definite height from its layout band; the icon
       ;; stays centered via align-self. In a content-height row there's no extra
       ;; height to stretch into, so short widgets (stats/ratios) look identical.
       [layouts/Row {:gap "8px" :class "align-items-stretch flex-grow-1" :style {:min-height 0}}
        (when icon
          [:div {:class "align-self-center"} [WidgetIcon {:icon icon :color color}]])
        ;; min-width:0 lets this flex child shrink below its content's intrinsic
        ;; width — without it a Vega chart measuring width:"container" reports the
        ;; over-wide flex box and the SVG bleeds past the card border. height:100%
        ;; + the flex column let a chart child fill the row band vertically too.
        [:div {:class "flex-grow-1"
               :style {:min-width 0 :min-height 0 :height "100%"
                       :display "flex" :flex-direction "column"}}
         children]]
       (when subtitle
         (let [content (expand-swatches subtitle)
               props {:variant text/Body-Sm-Regular :style {:color colors/neutral-400}}]
           (if (vector? content)
             (into [text/Text props] content)
             [text/Text props content])))])))

;; =============================================================================
;; Widget types
;; =============================================================================

(defnc StatWidget
  "One value, shown big. :fn returns the value — a NUMBER (locale-formatted with
   no decimals), or a STRING it formatted itself with `s/format-number` (for a
   prefix/suffix/percent, or a composite like \"73 / 20 / 7\") — or
   {:value v, ...extras} where the extras feed {key} interpolation in
   title/subtitle. Conditional formatting: return `:color` in the map to
   override the widget color from the value (e.g. red when negative) — it colors
   the icon and the number."
  [{:keys [spec result]}]
  (let [m (if (map? result) result {:value result})
        ;; presentational props from the :fn result (computed from the data) win
        ;; over the static spec — color/icon drive the tile
        color (or (:color m) (:color spec))
        icon (or (:icon m) (:icon spec))
        v (:value m)
        [title subtitle] (interpolate-texts spec m)]
    [WidgetShell {:icon icon :color color
                  :title title :subtitle subtitle :help (:help spec)}
     [text/Text {:variant text/Heading-Md
                 :el :div
                 :style {:color (or color colors/neutral-900)}}
      ;; nil renders a dash rather than "0": (* nil 4.33) JS-coerces to 0, so a
      ;; missing value must not read as a confident zero
      (cond (nil? v) "–"
            (number? v) (helpers/format-number v)
            :else (str v))]]))

(defnc TextWidget
  "Static markdown content (:md) — admin guidance, explainers. When a :fn is
   present, its (map) result feeds {key} interpolation inside the markdown,
   so prose can carry live numbers."
  [{:keys [spec result]}]
  (let [m (if (map? result) result {})
        [title subtitle] (interpolate-texts spec m)
        md (stats/interpolate (:md spec) (markup-interpolation-map m))]
    [WidgetShell {:icon (:icon spec) :color (:color spec)
                  :title title :subtitle subtitle :help (:help spec)}
     (when md
       [ui-html/SafeHTMLStrict {:html (markdown/markdown->html md)
                                :class ["nosco-redactor-text"
                                        "nosco-redactor-text--normal"]}])]))

(defnc HtmlWidget
  "Arbitrary HTML/CSS — heroes, callouts, CTAs, colored bands, computed
   tables, per-idea markup: the presentation markdown can't express.

   Two modes:
   - static `:html` string, with `{key}` interpolation from the :fn's map
     result (good for a banner with a live number);
   - fully DATA-DRIVEN: the :fn returns the HTML STRING itself, built from
     the data (loops, conditionals, computed rows). This wins over :html.

   The markup is sanitized with the STRICT DOMPurify profile (inline styles,
   images, data-* attributes kept; scripts AND iframes stripped) — surface
   HTML routinely interpolates USER-submitted idea fields, and the editor
   profile's unrestricted iframes would turn a malicious idea title into a
   stored frame injection shown to every viewer. The :fn runs in SCI with no
   JS interop — so it's arbitrary MARKUP, never arbitrary JS.
   No WidgetShell: landing chrome is full-bleed by design."
  [{:keys [spec result]}]
  (let [html (if (string? result)
               result                                  ; :fn built the HTML
               (stats/interpolate (:html spec)         ; static :html + {keys}
                                  (markup-interpolation-map (if (map? result) result {}))))]
    (when (and html (seq html))
      ;; nosco-surface-html (not the redactor class): we own the font + type
      ;; scale, the author owns color + layout.
      [ui-html/SafeHTMLStrict {:html html :class "nosco-surface-html"}])))

;; the platform sans (matches $font-family-sans-serif / the loaded @font-face);
;; Vega renders detached SVG, so pass a literal string — var(--nosco-font) won't
;; resolve there
(def ^:private chart-font "\"IBM Plex\", Helvetica, Arial, sans-serif")

(defn- with-nosco-palette
  "Default the chart to our brand look — the platform font everywhere, our blue
   for a single-series mark, and our `category-colors` palette for a categorical
   color encoding (instead of Vega's tableau default). The author's own :config
   always wins."
  [vl]
  (-> vl
      (update :config
              (fn [cfg]
                (merge {:font chart-font
                        :axis {:labelFont chart-font :titleFont chart-font}
                        :legend {:labelFont chart-font :titleFont chart-font}
                        :title {:font chart-font}
                        :mark {:color (first colors/category-colors)}
                        :range {:category (vec colors/category-colors)}}
                       cfg)))))

(defn desaturate-expr
  "A Vega expression that returns `color-expr` desaturated to ~`sat` saturation,
   preserving its hue and lightness (so muted marks that differ only in lightness
   stay distinguishable — a flat gray would merge them). `color-expr` is any Vega
   expression evaluating to a colour (a hex string, or scale('color', …)). Vega's
   hsl() parses the colour and reconstructs it at low saturation; leading '' + …
   coerces the colour object to a string for a :scale nil colour field."
  [color-expr sat]
  (str "'' + hsl(hsl(" color-expr ").h, " sat ", hsl(" color-expr ").l)"))

(defn- with-nosco-vega-config
  "Brand defaults for a RAW Vega spec (the :vega path): platform font on
   axes/legend/title and our category palette as the default categorical range.
   Shallow-merged under :config, so the author's own :config wins. The VL-only
   :mark color default doesn't apply — a raw Vega mark sets its own fill."
  [vega]
  (update vega :config
          (fn [cfg]
            (merge {:axis {:labelFont chart-font :titleFont chart-font}
                    :legend {:labelFont chart-font :titleFont chart-font}
                    :title {:font chart-font}
                    :range {:category (vec colors/category-colors)}}
                   cfg))))

;; A chart's cross-filter highlight rides one named signal per :on-click :filter
;; cell, in the cell's declared order: the i-th field's selected values live in
;; signal `nosco_dim_<i>` (an empty vector when nothing is selected). This is ONE
;; mechanism across both chart paths — on the :vl path with-dims emits VL variable
;; params of these names (VL compiles each to a Vega signal) and reads them in the
;; opacity/colour encoding; on the raw :vega path the author's spec declares the
;; same-named signals and reads them in its own encode. The engine only EXPOSES
;; and UPDATES the selection on the view (VegaEmbed :signals); the spec opts in by
;; reading it. `cross-filter-signals` builds the values map the runtime pushes.

(defn- dim-signal-name
  "The stable Vega signal name for the i-th cross-filter field."
  [i]
  (str "nosco_dim_" i))

(defn- cross-filter-signals
  "Map each cross-filter field (by its stable index → `nosco_dim_<i>`) to the
   current selection VALUES as a vector (empty when the cell has no selection).
   `fields` is the ordered :on-click :filter field list; `filter-values` is
   field→value-set. This is the runtime-pushed values map handed to VegaEmbed as
   `:signals` — shared by the :vl and raw :vega paths, which both read these
   signal names."
  [fields filter-values]
  (into {}
        (map-indexed (fn [i field] [(dim-signal-name i) (vec (get filter-values field))]))
        fields))

(defn- dim-test-expr
  "A CSP-safe Vega expression string that is true for a mark matching EVERY
   cross-filter field. For each `field` (index `i`) it tests the named signal
   `nosco_dim_<i>` (holding the selected values, empty when nothing is selected):
   an empty selection lights everything (`length(...)==0`), otherwise the mark's
   value must be present (`indexof(...)>=0`). Bracket indexing `datum['field']`
   tolerates dots in field names; `''+` coerces non-string datum values before
   the string compare. ANDed across every field."
  [fields]
  (str/join " && "
            (map-indexed
             (fn [i field]
               (let [nm (dim-signal-name i)]
                 (str "(length(" nm ")==0 || indexof(" nm ", ''+datum['" field "'])>=0)")))
             fields)))

(defn- with-dims
  "De-emphasise marks that don't match every cross-filter field. `fields` is the
   STATIC list of vega-column strings the chart cross-filters on (one per
   :on-click :filter cell) — derived from the spec, NOT from the live selection.

   For each field it emits a top-level VL variable param `nosco_dim_<i>` with a
   stable name and an empty default (each compiles to a Vega signal of the same
   name). Opacity rides a condition that tests those signals BY EXPRESSION
   (`dim-test-expr`), so a mark stays fully lit iff it matches every field and
   drops to low opacity otherwise. When the chart colours by a LITERAL-hex field
   (:scale nil, the data-driven-colour pattern) unmatched marks also desaturate
   to ~12% saturation, computed PER-MARK from its own colour so they keep their
   hue + lightness and stay distinguishable (opacity alone barely reads in a
   monochromatic palette; a flat gray would merge marks). A SCALED/scheme palette
   can't be desaturated this way — a data transform runs before scales resolve,
   so the resolved colour isn't reachable — so those charts keep the opacity dim
   alone.

   The selection VALUES are pushed into the signals at runtime
   (view.signal(name, v).run()); they are never baked into the spec. So the
   returned spec is identical (same hash) across selection changes — the chart
   updates via a signal pulse, never a re-embed. The author's own :opacity
   encoding, if any, wins (nothing is injected)."
  [vl fields]
  (if (and (seq fields)
           (nil? (get-in vl [:encoding :opacity])))
    (let [params (map-indexed (fn [i _] {:name (dim-signal-name i) :value []}) fields)
          test-expr (dim-test-expr fields)
          color (get-in vl [:encoding :color])
          cfield (:field color)
          literal? (and cfield (contains? color :scale) (nil? (:scale color)))]
      (cond-> (-> vl
                  (update :params (fnil into []) params)
                  (assoc-in [:encoding :opacity]
                            {:condition {:test test-expr :value 1} :value 0.3}))
        literal?
        (-> (update :transform (fnil conj [])
                    {:calculate (desaturate-expr (str "datum." cfield) 0.12) :as "__dim"})
            (assoc-in [:encoding :color]
                      {:condition (assoc color :test test-expr)
                       :field "__dim" :type "nominal" :scale nil :legend nil}))))
    vl))

(defnc SurfaceTooltip
  "A simple hover tooltip driven by a Vega hover event — the minimal Vega↔React
   bridge: `vega.cljs` installs a Vega tooltip handler that forwards the hovered
   mark's datum + cursor position here (see VegaEmbed's :on-hover-datum). We
   render the standard `.nosco-tooltip` at the cursor, portalled to <body> via
   the shared `usePortal` hook so no ancestor overflow/transform clips it,
   pointer-events off so it never eats the hover. Shows the row's :title (the
   bubbles themselves only show a short badge). A richer, spec-configurable
   hover card can replace this later — the bridge is the reusable part."
  [{:keys [datum x y]}]
  (let [Portal (.-Portal (usePortal))]
    [Portal
     [:div {:class "nosco-tooltip"
            :style {:position "fixed" :left (+ (or x 0) 12) :top (+ (or y 0) 12)
                    :z-index 10000 :pointer-events "none"}}
      [text/Text {:variant text/Body-Md-Regular} (:title datum)]]]))

(defnc ChartWidget
  "Chart widget: the author supplies EITHER a Vega-Lite fragment under :vl (mark
   + encoding — the common path) or a raw Vega spec under :vega (for what VL
   can't express, e.g. a force-transform beeswarm; :vega wins when both are
   present). The widget injects :data from the :fn rows and the chart fills its
   cell. A click forwards the clicked datum (keywordized) + the shift flag to
   `on-toggle`; the runtime's :on-click routing reads the datum's fields to
   filter or open an idea. When the chart cross-filters (its :on-click :filter),
   the current selection `filter-values` (a map of vega-column-string to selected
   value set) rides in as VegaEmbed `:signals` — the highlight for BOTH chart
   paths: the :vl path also bakes STATIC structure (named params + an opacity
   condition reading those signals) via `with-dims`, while a raw :vega spec reads
   the same-named signals (`nosco_dim_<i>`, one per :on-click :filter cell in
   order) in its own encode. Either way a toggle pulses a signal in place instead
   of re-embedding/re-parsing the chart."
  [{:keys [spec result filter-values on-toggle on-clear]}]
  (let [rows (vec result)
        data (mapv #(dissoc % :items) rows)
        ;; the STATIC cross-filter field list — order fixes the nosco_dim_<i>
        ;; signal <-> field mapping shared by with-dims (VL params) and the raw
        ;; :vega path (author-declared signals)
        fields (vec (vals (:filter (:on-click spec))))
        signals (cross-filter-signals fields filter-values)
        ;; hovered datum + cursor position, forwarded from Vega (raw :vega charts)
        [hover set-hover] (hooks/useState nil)
        [title subtitle] (interpolate-texts
                          spec {:total (stats/sum-by :value rows)
                                :count (count rows)})]
    ;; no :icon — charts need the full panel width, the icon column
    ;; squeezes the plot area badly
    [WidgetShell {:color (:color spec)
                  :title title :subtitle subtitle :help (:help spec)
                  :on-clear on-clear}
     (if (empty? rows)
       [text/Text {:variant text/Body-Sm-Regular :style {:color colors/neutral-400}}
        "No data"]
       [react/Suspense {:fallback (hx/f [loading/LoadingNew])}
        [VegaEmbed (cond-> {:data data
                            ;; author-chosen per-widget renderer (:svg default / :canvas)
                            :renderer (:renderer spec)
                            ;; the current cross-filter selection, pushed into the
                            ;; view's named signals in place — the highlight for
                            ;; BOTH paths (VL reads them in with-dims' encoding; a
                            ;; raw :vega spec reads the same-named signals)
                            :signals signals
                            :on-click-datum (when on-toggle
                                              (fn [datum shift?]
                                                (on-toggle {:datum datum :shift? shift?})))}
                     ;; bridge Vega hover → the React tooltip (raw :vega only, so
                     ;; :vl charts keep Vega's built-in text tooltip)
                     (:vega spec) (assoc :on-hover-datum set-hover)
                     (:vega spec) (assoc :vega-spec (with-nosco-vega-config (:vega spec)))
                     (not (:vega spec)) (assoc :vl-spec (-> (:vl spec) with-nosco-palette (with-dims fields))))]
        (when hover [SurfaceTooltip hover])])]))

;; run-widget only guards the :fn call — a malformed result can still throw
;; during RENDER (e.g. a :chart :fn returning a bare map). This boundary
;; keeps such a crash inside the widget card instead of unmounting the whole
;; reports page. Remounted via the :key in ResultsTab when the result
;; changes, so it retries after new data/Apply.
(hx/defcomponent WidgetErrorBoundary
  (constructor
   [this]
   (set! (. this -state) #js {:error nil})
   this)

  ^:static
  (getDerivedStateFromError
   (fn [error]
     #js {:error error}))

  (render
   [^js this]
   (let [error (gobj/get (.-state this) "error")]
     (if error
       [WidgetShell {:color colors/error-500
                     :title (or (gobj/get (.-props this) "title") "Widget")}
        [:pre {:style {:white-space "pre-wrap" :font-size "11px"}}
         (str "Widget failed to render: "
              (or (ex-message error) (str error)))]]
       (-> this .-props .-children)))))

(defnc Widget
  "Dispatch a run-widget entry to its renderer; per-widget errors render
   in place so one bad widget doesn't blank the panel. Interaction props
   (:filter-values / :on-toggle / :on-clear) only apply to charts."
  [{:keys [spec result error filter-values on-toggle on-clear]}]
  (cond
    error
    [WidgetShell {:icon (:icon spec) :color colors/error-500 :title (:title spec)}
     [:pre {:style {:white-space "pre-wrap" :font-size "11px"}}
      (str error)]]

    :else
    (case (:widget spec)
      :stat [StatWidget {:spec spec :result result}]
      :chart [ChartWidget {:spec spec :result result
                           :filter-values filter-values
                           :on-toggle on-toggle
                           :on-clear on-clear}]
      :text [TextWidget {:spec spec :result result}]
      :html [HtmlWidget {:spec spec :result result}]
      [:pre (str "Unknown :widget type: " (pr-str (:widget spec)))])))

(defnc IdeasWidget
  ":widget :ideas — real ideas rendered by idea-card/IdeaCardsContainer, so the
   surface list is indistinguishable from a plain platform ideas list: the
   container owns the idea MODAL (click opens a modal, not the page) and the
   list/grid(masonry)/table variants. It's presentational only — no
   search/sort/column-picker toolbar (those need server round-trips the surface
   doesn't do). Paginated CLIENT-SIDE: renders :page-size ideas (default 12)
   with the container's Show more, so a :limit :all source never dumps
   thousands of rows. `total-count` is the source's full match count; `spec`
   :fn optionally selects/orders; `master-form` gives the :table its field
   columns."
  [{:keys [spec result total-count master-form]}]
  ;; memoize so `ideas` keeps a STABLE identity across re-renders — the
  ;; container's resize-observer effect keys off it
  (let [ideas (hooks/useMemo (fn [] (vec (or result []))) [result])
        variant (or (:variant spec) (widgets-spec/default-for :ideas :variant))
        page-size (or (:page-size spec) (widgets-spec/default-for :ideas :page-size))
        [shown set-shown] (hooks/useState page-size)
        n (count ideas)
        visible (hooks/useMemo (fn [] (vec (take shown ideas))) [ideas shown])
        ;; the FULL count behind the source (may exceed what we loaded, e.g. a
        ;; numeric :limit); fall back to the loaded count when unavailable
        total (or total-count n)
        ;; the container reads :masterSubmissionForm off :ideabox for the table
        ;; columns; the modal reads the idea's own :ideabox/:stage
        ideabox {:masterSubmissionForm master-form}]
    [WidgetShell {:bare? true :title (:title spec) :hide-title (:hide-title spec)
                  :help (:help spec)}
     (if (empty? ideas)
       [text/Text {:variant text/Body-Sm-Regular :style {:color colors/neutral-400}}
        "No ideas"]
       [idea-card/IdeaCardsContainer
        {:ideas visible
         :ideabox ideabox
         :variant variant
         :total-count total
         :no-padding true
         :idea-card-options {:in :ideabox}
         ;; client-side pager: reveal the next slice of already-loaded data
         :on-show-more (when (< shown n)
                         (fn [] (set-shown (min n (+ shown page-size)))))}])]))

;; =============================================================================
;; :table widget — declarative columns over arbitrary rows
;; =============================================================================

(defn- idea-template-value
  "The raw submission-template value map for `field-id` on an idea (the shape
   ideas-table/render-template-value expects), or nil."
  [idea field-id]
  (some (fn [{:keys [ideaboxTemplateItem value]}]
          (when (= field-id (:id ideaboxTemplateItem)) value))
        (:template idea)))

(defn- field-sort-value
  "A comparable for sorting a :field column: the raw number for number fields,
   else the value's display text."
  [idea field-id]
  (let [v (idea-template-value idea field-id)]
    (or (:number v) (some-> v ideas-table/template-value->text))))

(defn- table-cell
  "Render one NON-title cell for a parsed column `col` (carrying ::src) on `row`
   (the raw row map). `results-map` is the idea's `get-results-map` output (nil
   unless the stage's eval config was loaded). Delegates to the SAME leaf
   renderers the ideas / eval tables use (render-template-value,
   render-evaluation-result-with-heatmap, render-cell-default) so the look stays
   in sync — titles are handled by ideas-table/render-idea-title directly."
  [col row master-form results-map]
  (let [{::keys [src] :keys [format]} col]
    (case (:kind src)
      :field
      (let [fid (:ref src)
            field (submission-form/find-by-id master-form fid)]
        (ideas-table/render-template-value
         (idea-template-value row fid) row
         {:template-field field
          :template-field-type (form-fields/->type (:type field))}))

      :eval
      (let [{:keys [ref option measure]} src]
        (if results-map
          ;; eval-center parity: the get-results-map value (formatted + the stage's
          ;; CONFIGURED heatmap colors), keyed by field-id (range/number) or
          ;; option-id (radio). nil (unscored) renders a dash.
          (table3/render-evaluation-result-with-heatmap
           (get results-map (keyword (or option ref))) row nil)
          ;; fallback (stage config not loaded): the raw score, UNTINTED. The
          ;; tint is the CHANNEL's to decide — the score comes from the server
          ;; and the colours from the field's own configured scale — so with no
          ;; config in hand there is nothing to apply, and inventing a default
          ;; ramp here would show a colour the channel never chose.
          (let [v (surface-table/column-raw-value src row {})
                ;; an evaluation column's format is DERIVED when the author
                ;; didn't state one: an option/heatmap column is a share, a score
                ;; keeps one decimal so the column aligns on the point
                fmt (or format (cond option {:style :percent}
                                     (= :heatmap measure) {:style :percent}
                                     :else {:decimals 1}))
                display (when (number? v) (helpers/format-number v fmt))]
            (table3/render-cell-default (or display "—")))))

      ;; :key / :fn / :none — arbitrary row data. A :fn may return hiccup (a
      ;; vector headed by a keyword/component) for a rich cell — render it inside
      ;; the same text style the default cell uses so it inherits the table's
      ;; font size/weight, rather than stringifying it.
      (let [v (surface-table/column-raw-value src row {})]
        (if (and (vector? v) (or (keyword? (first v)) (fn? (first v))))
          [text/Text {:variant text/Body-Md-Regular :class "text-neutral-700"} v]
          (table3/render-cell-default
           (cond (nil? v) "—"
                 ;; :format IS the s/format-number option map — a column can't
                 ;; call the formatter itself, so it names the arguments instead
                 (and format (number? v)) (helpers/format-number v format)
                 (number? v) (util/number-format v :default)
                 :else (str v))))))))

(defn- render-action-cell
  "A table action cell. `:toggle-set` renders a button that toggles the row's id
   into the collection `:state` cell named by `:bind` (via data-toggle-set,
   handled by the surface's delegated click) — e.g. a shortlist / compare tray.
   Reads the LIVE control value (`active?`) so it fills (★) when the row is
   already in the set, outline (☆) otherwise — the parent table reflects the
   same shortlist."
  [{:keys [action bind]} row active?]
  (case action
    :toggle-set
    [:button {:data-toggle-set (str (name bind) ":" (:id row))
              :style {:cursor "pointer" :background "none" :border "none"
                      :font-size "18px" :line-height 1
                      :color (if active? "var(--nosco-brand)" "var(--nosco-text-subtle)")}}
     (if active? "★" "☆")]
    nil))

(def ^:private table-page-size 20)

(defnc SurfaceTableWidget
  ":widget :table — a declarative table over the source's rows (ideas, enriched
   ideas, or synthetic maps), rendered through the SAME table3 machinery as the
   evaluation-results / ideas tables: TableUnwrapped with a sticky header + first
   column, the shared leaf cell renderers, and ideas-table/render-idea-title for
   the title (opens the idea modal; cmd-click follows the href). Columns name a
   value source (:field/:eval/:key/:fn — nosco.surface.table) plus
   presentation; omit :columns (or :columns :auto) to derive them from the rows'
   evaluation data. Sorted once by :sort {:col <title> :dir}."
  [{:keys [spec result master-form set-modal eval-template heatmap-config-fields
           control-values]}]
  (let [rows (hooks/useMemo (fn [] (vec (or result []))) [result])
        author-cols (:columns spec)
        cols0 (if (or (nil? author-cols) (= :auto author-cols))
                (surface-table/auto-columns rows eval-template)
                author-cols)
        ;; assign a stable render key + parse each column's source ONCE
        cols (hooks/useMemo
              (fn []
                (vec (map-indexed
                      (fn [i c] (assoc c ::key (str "c" i)
                                       ::src (surface-table/parse-column-source c)))
                      cols0)))
              [cols0])
        ;; static initial sort (interactive header sort is a fast-follow)
        sorted (hooks/useMemo
                (fn []
                  (if-let [{:keys [col dir]} (:sort spec)]
                    (if-let [c (some #(when (= col (:title %)) %) cols)]
                      (surface-table/sort-rows
                       rows #(surface-table/column-raw-value (::src c) % {:field-value field-sort-value})
                       (or dir :desc))
                      rows)
                    rows))
                [rows cols (:sort spec)])
        ;; paginate: render a page of rows at a time with a Show-more button (as
        ;; the ideas / eval tables do), so a large channel doesn't mount every
        ;; row. Reset to the first page whenever the row set changes (a filter).
        [visible set-visible] (hooks/useState table-page-size)
        _ (hooks/useEffect (fn [] (set-visible table-page-size) js/undefined) [sorted])
        ;; open the surface idea modal at the clicked row — the reference
        ;; on-click-idea (a cmd-click still follows the href to the idea page).
        on-click-idea (hooks/useCallback
                       (fn [e {:keys [idea-idx]}]
                         (when-not (util/browser-handles-click? e)
                           (.preventDefault e)
                           (.stopPropagation e)
                           (when set-modal (set-modal {:ideas sorted :idx idea-idx}))))
                       [sorted set-modal])
        href-fn (fn [idea query-params]
                  (router/href :nosco.routes.user/ideas-single {:idea-id (:id idea)} query-params))
        title-key (some (fn [{::keys [key] :keys [link]}] (when (= :idea link) key)) cols)
        table-cols (mapv (fn [{::keys [key] :keys [title width align link]}]
                           {:key key :title title
                            :constraints (cond-> {:align (or align :left)}
                                           ;; an explicit author :width wins on ANY column
                                           width (assoc :width width)
                                           ;; the link/title column is the FLUID one
                                           ;; (no width → takes the remaining space, so it
                                           ;; grows to fill the table); a floor keeps it
                                           ;; from collapsing
                                           (and (nil? width) link) (assoc :min-width 280)
                                           ;; every other width-less column defaults to a
                                           ;; FIXED width, so the title keeps the slack
                                           ;; (matches the ideas / eval tables)
                                           (and (nil? width) (not link)) (assoc :width 150))})
                         cols)
        render-cells (into {}
                           (map (fn [{::keys [key] :keys [action] :as c}]
                                  [key (cond
                                         ;; reuse the ideas table's title cell — pass the
                                         ;; idea's title + the wrapper (carries :data +
                                         ;; ::ideas-table/idx) explicitly.
                                         (= key title-key)
                                         (fn [_v wrapper _col]
                                           (ideas-table/render-idea-title
                                            on-click-idea href-fn
                                            (:title (:data wrapper)) wrapper))
                                         action
                                         (fn [_v wrapper _col]
                                           (render-action-cell
                                            c (:data wrapper)
                                            (contains? (get control-values (:bind c))
                                                       (:id (:data wrapper)))))
                                         :else
                                         (fn [_v wrapper _col]
                                           (table-cell c (:data wrapper) master-form
                                                       (::results-map wrapper))))]))
                           cols)
        ;; row wrapper: table3 reads :id/:data; render-idea-title reads :data +
        ;; ::ideas-table/idx off it; ::results-map holds the idea's configured
        ;; eval-cell values (get-results-map) when the stage config was loaded.
        data (vec (map-indexed
                   (fn [i row]
                     (cond-> {:id (or (:id row) i) :data row ::ideas-table/idx i}
                       eval-template (assoc ::results-map
                                            (eval-util/get-results-map
                                             row eval-template heatmap-config-fields))))
                   sorted))
        ;; only the first `visible` rows are mounted; the modal still receives the
        ;; full `sorted` list (the row idx indexes into it), so paging never
        ;; breaks next/prev navigation.
        shown (vec (take visible data))
        remaining (- (count data) visible)]
    [WidgetShell {:bare? true :title (:title spec) :hide-title (:hide-title spec)
                  :help (:help spec)}
     (if (empty? rows)
       [text/Text {:variant text/Body-Sm-Regular :style {:color colors/neutral-400}} "No rows"]
       [table3/GrayTableWrap {:class "pb-spacing-16 position-relative"}
        [table3/TableUnwrapped {:columns table-cols
                                :data shown
                                :render-cells render-cells
                                :sticky-first-column? true
                                :sticky-header? true}]])
     (when (pos? remaining)
       [:div {:style {:display "flex" :justify-content "center"}}
        [table3/ShowMoreButton
         {:label (str "Show " (min table-page-size remaining) " more")
          :on-click #(set-visible (+ visible table-page-size))}]])]))

(defnc FilterPills
  "The spaces-style colored pill bar (spaces-tabs/Tab). Single and multi render
   the SAME pill row — only the click behaviour differs:
   - SINGLE (`active` is a scalar id): a click selects just that pill (writes the
     id); clicking the active pill clears it (writes nil = All).
   - MULTI (`multi?`, `active` is a SET of ids): plain click selects only that
     pill (replace); shift-click adds/removes it (toggle). An empty set = All."
  [{:keys [options active on-change multi? compact]}]
  (let [active-set (cond multi?        (if (set? active) active #{})
                         (some? active) #{active}
                         :else          #{})
        pill (fn [{:keys [id title color]} selected on-click]
               [:button {:key id :role "tab" :class "button-reset"
                         :aria-selected selected :on-click on-click}
                [spaces-tabs/Tab {:label title :color color
                                  :selected selected :compact compact}]])]
    [layouts/Row {:gap "6px" :class "flex-wrap align-items-center"}
     (for [{:keys [id] :as opt} options]
       (pill opt (contains? active-set id)
             (if multi?
               (fn [e] (on-change id (.-shiftKey e)))
               (fn [_e] (on-change (if (= active id) nil id))))))]))

(defn- ->select-option
  "Surface option {:id :title :color} → select3 option {:label :value :color}."
  [{:keys [id title color]}]
  (cond-> {:label title :value id} color (assoc :color color)))

(defnc FilterWidget
  ":widget :filter — tab pills / spaces-style pills / our custom select3
   dropdown, emitting a filter descriptor (or, for a :control, a raw value).
   `multi?` switches the dropdown/pills to multi-select: on-change emits the
   toggled option id (the runtime toggles it into the cell's id-SET), and nil
   still means clear/All. Options come from the :options source.

   Pills always render the COMPACT preset: a control is chrome around the data,
   not the data, and it reads that way everywhere it appears — so this is a
   house look rather than something each widget re-decides."
  [{:keys [spec options active on-change multi?]}]
  (let [active-set (if (set? active) active #{})]
    [WidgetShell {:bare? true :title (:title spec) :hide-title (:hide-title spec)
                  :help (:help spec)}
     (case (:variant spec)
       ;; our custom dropdown (select3), NOT a native <select>
       :dropdown
       [:div {:style {:max-width "280px"}}
        (if multi?
          [select3/Select
           {:items* {:items (mapv (fn [{:keys [id] :as o}]
                                    (assoc (->select-option o)
                                           :selected (contains? active-set id)))
                                  options)}
            :item-title-key :label
            :item-value-key :value
            :item-selected-key :selected
            :item-color-key :color
            :placeholder "All"
            ;; a checkbox dropdown is inherently multi — each toggle adds/removes
            ;; (shift? = true), unlike pills where plain click is single-select
            :on-change (fn [item] (on-change (:value item) true))}]
          [select3/SelectSimple
           {:value active
            :placeholder "All"
            :options (mapv ->select-option options)
            :item-color-key :color
            :on-change (fn [{:keys [value]}] (on-change value))
            :on-remove (fn [] (on-change nil))}])]
       ;; default (nil / :pills): the coloured spaces-style pill bar
       [FilterPills {:options options :active active :on-change on-change :multi? multi?
                     :compact true}])]))

(defnc DateRangeControl
  "The :date-range control producer — two native date inputs publishing a
   `{:from iso :to iso}` value (either side may be nil for an open range)."
  [{:keys [spec value on-change]}]
  (let [{:keys [from to]} value
        set-side (fn [side]
                   (fn [e]
                     (let [v (.. e -target -value)
                           v (when (seq v) v)
                           next (assoc (or value {}) side v)]
                       ;; both sides blank → publish nil (dissoc the control
                       ;; state → fall back to :default). {:from nil :to nil}
                       ;; is truthy, so not-empty can't clear it.
                       (on-change (when (some some? (vals next)) next)))))]
    [WidgetShell {:bare? true :title (:title spec) :hide-title (:hide-title spec)
                  :help (:help spec)}
     [layouts/Row {:gap "8px" :class "align-items-center"}
      [:input {:type "date" :value (or from "") :on-change (set-side :from)}]
      [text/Text {:variant text/Body-Sm-Regular :style {:color colors/neutral-400}} "→"]
      [:input {:type "date" :value (or to "") :on-change (set-side :to)}]]]))

(defnc IdeaModal
  "Surface idea modal — ALWAYS MOUNTED (only the dialog's visibility toggles),
   the same structure as ideabox_reports/IdeaModalContainer, so IdeaModalNew's
   internal node-ref stays stable. (A freshly-mounted-on-open modal imbalanced
   toast/use-toast-context's conj/pop → \"Can't pop empty vector\".) IdeaModalNew
   provides the prev/next pager over the surface's ideas list."
  [{:keys [modal set-modal]}]
  (let [{:keys [ideas idx]} modal]
    [idea-card/IdeaModalNew
     {:modal-idea (when (number? idx) (get ideas idx))
      :modal-idea-idx idx
      ;; IdeaModalNew calls this with dec/inc (prev/next) or nil (close)
      :set-modal-idea-idx (fn [next]
                            (set-modal
                             (fn [m]
                               (let [new-idx (if (fn? next) (next (:idx m)) next)]
                                 (when (number? new-idx) (assoc m :idx new-idx))))))
      :total-count (count ideas)
      :visible-count (count ideas)
      :on-load-next-page (fn [_] nil)
      :loading false
      :on-close #(set-modal nil)}]))

(defnc WidgetSkeleton
  "Placeholder that fills the widget's (already grid-sized) cell while its
   source loads — the layout is known up front, so the page keeps its shape
   and the data streams into place."
  []
  [:div {:style {:flex "1 1 auto" :min-height "72px" :display "flex"}}
   [skeleton/Rect {:width "100%" :height "100%" :border-radius "8px"}]])

;; =============================================================================
;; New-runtime components (engine port)
;; =============================================================================

(defnc SurfaceLoadingIndicator
  "The ONE surface-level loading/refreshing cue: shown while any node is
   still :loading (initial load / a control-driven refetch) or while a
   refresh! is in flight (`refreshing?` — the engine keeps serving STALE data
   then, so this bar is the only visible cue). Per-widget skeletons stay; this
   is the global signal."
  [{:keys [loading? refreshing?]}]
  (when (or loading? refreshing?)
    [layouts/Row {:gap "6px" :class "align-items-center"
                  :style {:padding "2px 0" :min-height "20px"}}
     [:span {:class "spinner-border spinner-border-sm text-secondary"
             :role "status" :aria-label "loading"}]
     [text/Text {:variant text/Body-Sm-Regular :style {:color colors/neutral-400}}
      (if refreshing? "Refreshing…" "Loading…")]]))

(defnc ControlWidget
  ":widget :control — the PURE renderer of a :state cell's value:
   variant/options live on the widget spec, the current `value` comes from the
   cell, and `on-change` (wired to the engine by the runtime) fires ONLY on
   user interaction — no render-time writes, no auto-select effects (a
   defaultless cell stays empty until the user picks; derived defaults are a
   graph fact). `options` is the resolved options vector
   ([{:id .. :title .. :color ..}]) — the same shape a :filter widget uses.
   `multi?` (from `:select :multi`) makes the dropdown/pills multi-select over a
   SET-valued cell; `value` is then the id-set."
  [{:keys [spec options value on-change multi?]}]
  (if (= :date-range (:variant spec))
    [DateRangeControl {:spec spec :value value :on-change on-change}]
    [FilterWidget {:spec (update spec :variant #(if (= :date-range %) :dropdown (or % :dropdown)))
                   :options options
                   :active value
                   :multi? multi?
                   :on-change on-change}]))
